/**
 * Gallery — the fullscreen image viewer, shared by every detail screen.
 *
 * Any screen that shows pictures gets the same behaviour by rendering
 * `data-gallery="<index>"` on whatever should open the viewer (a hero image, a
 * thumbnail in a rail) and calling `Gallery.attach( state, images )`. Clicks are
 * delegated from the content slot, so triggers may be anywhere on the screen
 * and may be re-rendered underneath.
 *
 * The viewer builds its own markup rather than expecting each template to carry
 * a copy: it is chrome, not content, and a second hand-written copy of it is a
 * second place for the credit line to be forgotten. Imported images almost
 * always come with a mandatory copyright string — showing it is a licence
 * condition, not decoration.
 *
 * One <img> is re-pointed as the guest pages through the list; a phone should
 * not hold ten full-size bitmaps to show one.
 *
 * Listeners go through the owning State (tracked cleanup); the element itself
 * lives in the device frame rather than in the screen — inside the scrolling
 * page an overlay would scroll away with the content behind it — so the state
 * must call `destroy()` on exit, which the shell's own cleanup cannot do.
 */
import { _lcs } from "../i18n";
import { Fullscreen } from "./Fullscreen";

export class Gallery
{
    /** How far a swipe must travel to count as paging, in pixels. */
    static SWIPE_THRESHOLD = 40;

    /**
     * @param {IF.State} state Owns the listeners and must call destroy() on exit
     * @param {Array} images [ { url, alt, credit } ] in display order
     * @returns {Gallery|null} null when there is nothing to show
     */
    static attach( state, images )
    {
        const list = ( images || [] ).filter( ( image ) => image && image.url );
        return list.length > 0 ? new Gallery( state, list ) : null;
    }

    constructor( state, images )
    {
        this.state = state;
        this.app = state.app;
        this.images = images;
        this.index = 0;

        this._build();
        this._bind();
    }

    _build()
    {
        const hasMany = this.images.length > 1;

        const el = document.createElement( "div" );
        el.className = "lightbox";
        el.hidden = true;
        el.innerHTML =
            '<div class="lightbox__scrim" data-id="close"></div>' +
            '<figure class="lightbox__figure">' +
                '<img class="lightbox__img" data-id="img" src="" alt="" />' +
                '<figcaption class="lightbox__caption"><span data-id="credit"></span></figcaption>' +
            '</figure>' +
            '<button class="detail__round lightbox__close" type="button" data-id="close">' +
                '<i class="icon icon--x"></i>' +
            '</button>' +
            ( hasMany
                ? '<button class="lightbox__nav lightbox__nav--prev" type="button" data-id="prev">' +
                      '<i class="icon icon--arrow-left"></i>' +
                  '</button>' +
                  '<button class="lightbox__nav lightbox__nav--next" type="button" data-id="next">' +
                      '<i class="icon icon--arrow-right"></i>' +
                  '</button>' +
                  '<span class="lightbox__count" data-id="count"></span>'
                : "" );

        // The labels go on with setAttribute rather than into the HTML string:
        // a translation is data, and data does not belong in markup being parsed.
        el.querySelector( '.lightbox__close' ).setAttribute( "aria-label", _lcs( "Schließen" ) );
        if ( hasMany )
        {
            el.querySelector( '[data-id="prev"]' ).setAttribute( "aria-label", _lcs( "Vorheriges Bild" ) );
            el.querySelector( '[data-id="next"]' ).setAttribute( "aria-label", _lcs( "Nächstes Bild" ) );
        }

        const content = this.app.view.pageContent;
        const frame = ( content && content.closest( ".ga" ) ) || document.body;
        frame.appendChild( el );

        this.el = el;
        this.img = el.querySelector( '[data-id="img"]' );
        this.credit = el.querySelector( '[data-id="credit"]' );
        this.count = el.querySelector( '[data-id="count"]' );
    }

    _bind()
    {
        const state = this.state;

        // Delegated from the content slot: the hero, a thumbnail rail and
        // anything a screen adds later all work without re-binding.
        state.addEventListener( this.app.view.pageContent, "click", ( event ) =>
        {
            const trigger = event.target.closest( "[data-gallery]" );
            if ( trigger )
            {
                this.open( +trigger.getAttribute( "data-gallery" ) );
            }
        } );

        this.el.querySelectorAll( '[data-id="close"]' ).forEach( ( button ) =>
            state.addEventListener( button, "click", () => this.close() ) );

        const prev = this.el.querySelector( '[data-id="prev"]' );
        const next = this.el.querySelector( '[data-id="next"]' );
        if ( prev ) state.addEventListener( prev, "click", () => this.step( -1 ) );
        if ( next ) state.addEventListener( next, "click", () => this.step( 1 ) );

        state.addEventListener( document, "keydown", ( event ) => this._onKey( event ) );

        // Swipe — the primary gesture on the phone this app is built for.
        state.addEventListener( this.el, "touchstart", ( event ) =>
        {
            this._touchX = event.changedTouches[ 0 ].clientX;
        } );
        state.addEventListener( this.el, "touchend", ( event ) =>
        {
            if ( typeof this._touchX !== "number" )
            {
                return;
            }
            const delta = event.changedTouches[ 0 ].clientX - this._touchX;
            this._touchX = null;

            if ( Math.abs( delta ) > Gallery.SWIPE_THRESHOLD )
            {
                this.step( delta < 0 ? 1 : -1 );
            }
        } );
    }

    /* ------------------------------- Public API ------------------------------ */

    open( index )
    {
        if ( !this.el )
        {
            return;
        }
        this.show( index );
        this.el.hidden = false;
        requestAnimationFrame( () => this.el.classList.add( "lightbox--open" ) );
        // Opened by a tap, so the browser allows it: the image gets the whole
        // screen where element fullscreen exists (not on iPhone — see Fullscreen).
        Fullscreen.enter( this.el );
    }

    close()
    {
        if ( !this.el )
        {
            return;
        }
        this.el.classList.remove( "lightbox--open" );
        this.el.hidden = true;
        Fullscreen.exit();
    }

    /** Wraps around: paging past the last image returns to the first. */
    step( delta )
    {
        const count = this.images.length;
        if ( count < 2 )
        {
            return;
        }
        this.show( ( this.index + delta + count ) % count );
    }

    show( index )
    {
        const image = this.images[ index ];
        if ( !image )
        {
            return;
        }

        this.index = index;
        this.img.src = image.url;
        this.img.alt = image.alt || "";
        this.credit.textContent = image.credit || "";

        if ( this.count )
        {
            this.count.textContent = ( index + 1 ) + " / " + this.images.length;
        }
    }

    destroy()
    {
        if ( this.el )
        {
            this.el.remove();
            this.el = null;
        }
    }

    _onKey( event )
    {
        if ( !this.el || this.el.hidden )
        {
            return;
        }

        if ( event.key === "Escape" )          this.close();
        else if ( event.key === "ArrowLeft" )  this.step( -1 );
        else if ( event.key === "ArrowRight" ) this.step( 1 );
    }
}
